import React from "react";

import {
  FaEdit,
  FaExternalLinkAlt,
  FaTrash,
} from "react-icons/fa";

import {
  BillingItemTypeBadge,
  LifecycleStatusBadge,
  PaymentStatusBadge,
  RenewalStatusBadge,
} from "../ui/SubscriptionBadge";

import {
  calculateSubscriptionProgress,
} from "../utils/subscriptionDates";

import {
  formatSubscriptionDate,
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

function MobileDocumentLink({
  href,
  label,
}) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 dark:border-slate-700 dark:text-slate-300"
    >
      {label}

      <FaExternalLinkAlt />
    </a>
  );
}

export default function SubscriptionsMobileList({
  subscriptions = [],
  isLoading = false,
  deletingId = null,
  onEdit,
  onDelete,
}) {
  if (isLoading) {
    return (
      <div className="space-y-3 p-4 lg:hidden">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-64 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!subscriptions.length) {
    return (
      <div className="grid min-h-[240px] place-items-center p-6 text-center lg:hidden">
        <div>
          <p className="font-bold text-slate-700 dark:text-slate-200">
            Aucun résultat
          </p>

          <p className="mt-1 text-sm text-slate-400">
            Modifie les filtres pour afficher
            d’autres éléments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 lg:hidden">
      {subscriptions.map((subscription) => {
        const progress =
          calculateSubscriptionProgress({
            itemType: subscription.item_type,
            startDate: subscription.start_date,
            endDate: subscription.end_date,
          });

        return (
          <article
            key={subscription.id}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-black text-slate-950 dark:text-white">
                  {getAssociationName(
                    subscription,
                  )}
                </p>

                <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
                  {subscription.product}
                </p>
              </div>

              <span className="shrink-0 text-xs text-slate-400">
                #{subscription.id}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <BillingItemTypeBadge
                itemType={subscription.item_type}
              />

              <LifecycleStatusBadge
                status={subscription.status}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Montant
                </p>

                <p className="mt-1 font-black text-slate-950 dark:text-white">
                  {formatSubscriptionMoney(
                    subscription.total_amount,
                  )}
                </p>
              </div>

              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Reste à payer
                </p>

                <p
                  className={[
                    "mt-1 font-black",
                    subscription.is_overdue
                      ? "text-red-600 dark:text-red-300"
                      : "text-slate-950 dark:text-white",
                  ].join(" ")}
                >
                  {formatSubscriptionMoney(
                    subscription.remaining_amount,
                  )}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <PaymentStatusBadge
                status={
                  subscription.payment_status
                }
              />

              {subscription.item_type ===
                "subscription" && (
                <RenewalStatusBadge
                  status={
                    subscription.renewal_status
                  }
                />
              )}
            </div>

            <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
              {subscription.item_type ===
              "one_time" ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Date :{" "}
                  <strong className="text-slate-800 dark:text-slate-200">
                    {formatSubscriptionDate(
                      subscription.start_date,
                    )}
                  </strong>
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      {formatSubscriptionDate(
                        subscription.start_date,
                      )}
                    </span>

                    <span>
                      {formatSubscriptionDate(
                        subscription.end_date,
                      )}
                    </span>
                  </div>

                  {progress && (
                    <div className="mt-2">
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                        <div
                          className={[
                            "h-full rounded-full",
                            progress.isExpired
                              ? "bg-red-500"
                              : progress.percentage >=
                                  85
                                ? "bg-orange-500"
                                : "bg-indigo-500",
                          ].join(" ")}
                          style={{
                            width: `${progress.percentage}%`,
                          }}
                        />
                      </div>

                      <p className="mt-1 text-right text-[11px] text-slate-400">
                        {progress.isExpired
                          ? "Abonnement expiré"
                          : `${progress.daysRemaining} jours restants`}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
              <MobileDocumentLink
                href={
                  subscription.drive_quote_link
                }
                label="Devis"
              />

              <MobileDocumentLink
                href={
                  subscription.drive_invoice_link
                }
                label="Facture"
              />

              <button
                type="button"
                onClick={() =>
                  onEdit?.(subscription)
                }
                className="ml-auto inline-flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300"
              >
                <FaEdit />

                Modifier
              </button>

              <button
                type="button"
                onClick={() =>
                  onDelete?.(subscription)
                }
                disabled={
                  Number(deletingId) ===
                  Number(subscription.id)
                }
                className="grid h-9 w-9 place-items-center rounded-xl bg-red-50 text-red-600 disabled:cursor-wait disabled:opacity-50 dark:bg-red-950/40 dark:text-red-300"
                aria-label="Supprimer"
              >
                <FaTrash />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}